import { useAuth } from '@/contexts/AuthContext';
import { ADMIN_PAGES } from '@/config/adminPages';

// Default permissions for non-admin users (most restrictive)
const DEFAULT_PERMISSIONS = {
  patients: { view: true, create: false, edit: false, delete: false },
  surgeries: { view: true, create: false, edit: false, delete: false },
  appointments: { view: true, create: true, edit: true, delete: true, manage_reminders: false },
  clinical_history: { view: true, create: false, edit: false, delete: false },
  slots: { manage: false, manage_others: false },
  billing: { view: false },
  prices: { view: false },
  exports: { allowed: false },
  monitoring: { view: false },
  users: { view: false, manage: false },
  // Cada página del registro `ADMIN_PAGES` obtiene por defecto `false`
  // — el superadmin activa lo que quiera desde el editor de permisos.
  admin_pages: Object.fromEntries(ADMIN_PAGES.map((p) => [p.key, false])),
};

// Predefined permission templates
export const PERMISSION_TEMPLATES = [
  {
    key: 'recepcionista',
    label: 'Recepcionista',
    description: 'Gestiona citas y datos basicos de pacientes',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    permissions: {
      patients: { view: true, create: true, edit: true, delete: false },
      surgeries: { view: true, create: false, edit: false, delete: false },
      appointments: { view: true, create: true, edit: true, delete: true, manage_reminders: true },
      clinical_history: { view: false, create: false, edit: false, delete: false },
      slots: { manage: true, manage_others: true },
      billing: { view: true },
      prices: { view: true },
      exports: { allowed: false },
      monitoring: { view: false },
      certificates: { create: true },
      consents: { create: true },
    },
  },
  {
    key: 'enfermera',
    label: 'Enfermera',
    description: 'Acceso clinico completo sin gestion economica',
    color: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    permissions: {
      patients: { view: true, create: true, edit: true, delete: false },
      surgeries: { view: true, create: true, edit: true, delete: false },
      appointments: { view: true, create: true, edit: true, delete: true, manage_reminders: true },
      clinical_history: { view: true, create: true, edit: true, delete: false },
      slots: { manage: false, manage_others: false },
      billing: { view: false },
      prices: { view: false },
      exports: { allowed: false },
      monitoring: { view: false },
      certificates: { create: true },
      consents: { create: true },
    },
  },
  {
    key: 'doctor_colaborador',
    label: 'Doctor Colaborador',
    description: 'Acceso amplio con historial clinico y precios',
    color: 'bg-purple-100 text-purple-700 border-purple-200',
    permissions: {
      patients: { view: true, create: true, edit: true, delete: false },
      surgeries: { view: true, create: true, edit: true, delete: false },
      appointments: { view: true, create: true, edit: true, delete: true, manage_reminders: true },
      clinical_history: { view: true, create: true, edit: true, delete: true },
      slots: { manage: true, manage_others: true },
      billing: { view: true },
      prices: { view: true },
      exports: { allowed: true },
      monitoring: { view: false },
      certificates: { create: true },
      consents: { create: true },
    },
  },
  {
    key: 'solo_lectura',
    label: 'Solo Lectura',
    description: 'Puede ver todo pero no modificar nada',
    color: 'bg-gray-100 text-gray-700 border-gray-200',
    permissions: {
      patients: { view: true, create: false, edit: false, delete: false },
      surgeries: { view: true, create: false, edit: false, delete: false },
      appointments: { view: true, create: false, edit: false, delete: false, manage_reminders: false },
      clinical_history: { view: true, create: false, edit: false, delete: false },
      slots: { manage: false, manage_others: false },
      billing: { view: true },
      prices: { view: true },
      exports: { allowed: false },
      monitoring: { view: true },
    },
  },
];

// All permissions structure (for the editor UI)
export const PERMISSION_MODULES = [
  {
    key: 'patients',
    label: 'Pacientes',
    actions: [
      { key: 'view', label: 'Ver' },
      { key: 'create', label: 'Crear' },
      { key: 'edit', label: 'Editar' },
      { key: 'delete', label: 'Eliminar' },
    ],
  },
  {
    key: 'surgeries',
    label: 'Cirugías',
    actions: [
      { key: 'view', label: 'Ver' },
      { key: 'create', label: 'Crear' },
      { key: 'edit', label: 'Editar' },
      { key: 'delete', label: 'Eliminar' },
    ],
  },
  {
    key: 'appointments',
    label: 'Citas / Agenda',
    actions: [
      { key: 'view', label: 'Ver' },
      { key: 'create', label: 'Crear' },
      { key: 'edit', label: 'Editar' },
      { key: 'delete', label: 'Eliminar' },
      { key: 'manage_reminders', label: 'Enviar recordatorios (otros doctores)' },
    ],
  },
  {
    key: 'slots',
    label: 'Horarios (Slots)',
    actions: [
      { key: 'manage', label: 'Crear / Editar / Bloquear' },
      { key: 'manage_others', label: 'Crear para otros doctores' },
    ],
  },
  {
    key: 'clinical_history',
    label: 'Historia Clínica',
    actions: [
      { key: 'view', label: 'Ver' },
      { key: 'create', label: 'Crear' },
      { key: 'edit', label: 'Editar' },
      { key: 'delete', label: 'Eliminar' },
    ],
  },
  {
    key: 'billing',
    label: 'Facturación',
    actions: [{ key: 'view', label: 'Ver' }],
  },
  {
    key: 'prices',
    label: 'Precios',
    actions: [{ key: 'view', label: 'Ver' }],
  },
  {
    key: 'exports',
    label: 'Exportar Datos',
    actions: [
      { key: 'allowed', label: 'Permitido' },
      { key: 'send_surgery_email', label: 'Enviar programación quirúrgica por email' },
    ],
  },
  {
    key: 'monitoring',
    label: 'Monitoreo',
    actions: [{ key: 'view', label: 'Ver' }],
  },
  {
    key: 'certificates',
    label: 'Justificantes',
    actions: [
      { key: 'create', label: 'Crear / Imprimir' },
    ],
  },
  {
    key: 'consents',
    label: 'Consentimientos (RGPD)',
    actions: [
      { key: 'create', label: 'Crear / Imprimir' },
    ],
  },
  // ─── Páginas del administrador (derivadas del registro central) ───
  // Cada entrada de `ADMIN_PAGES` aparece automáticamente como un toggle
  // aquí, sin cambios en este archivo. Regla: al añadir una página nueva
  // a la consola, basta con registrarla en `/app/frontend/src/config/adminPages.js`
  // y el editor de permisos la muestra desactivada por defecto.
  {
    key: 'admin_pages',
    label: 'Páginas del administrador',
    description: 'Toggles individuales por página. Por defecto ninguno activo — el superadmin habilita lo que considere.',
    actions: ADMIN_PAGES.map((p) => ({
      key: p.key,
      label: p.label,
      description: p.description,
    })),
  },
];

export function usePermissions() {
  const { admin, isAdmin, isDoctor } = useAuth();

  const can = (module, action = 'view') => {
    if (isAdmin) return true;
    // Doctors always have implicit slots.manage (for their own slots)
    if (isDoctor && module === 'slots' && action === 'manage') return true;
    const perms = admin?.permissions || DEFAULT_PERMISSIONS;
    const modulePerms = perms[module] || {};
    return !!modulePerms[action];
  };

  const canAccessLocation = (locationId) => {
    if (isAdmin) return true;
    const allowed = admin?.allowed_locations;
    // If doctor has explicit allowed_locations, use that
    if (allowed && allowed.length > 0) return allowed.includes(locationId);
    // For doctors without allowed_locations, restrict to their default location
    if (isDoctor && admin?.default_location_id) return locationId === admin.default_location_id;
    // Fallback: allow all
    return true;
  };

  return {
    can,
    canAccessLocation,
    isAdmin,
    isDoctor,
    permissions: admin?.permissions || DEFAULT_PERMISSIONS,
    allowedLocations: admin?.allowed_locations || [],
  };
}

export function getDefaultPermissions() {
  return JSON.parse(JSON.stringify(DEFAULT_PERMISSIONS));
}
